import { ROUTES } from '@/constants/routes';
import { createAppRouter } from '@/platform/createAppRouter';
import ProtectedRoute from './ProtectedRoute';

// Layouts
import { StudentAreaWrapper } from '@/pages/student/StudentAreaWrapper';
import { CompanyLayout } from '@/components/templates/CompanyLayout';
import { BackofficeLayout } from '@/components/templates/BackofficeLayout';

// Auth pages
import { AuthI18nLayout } from '@/pages/auth/AuthI18nLayout';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ErrorPreviewPage } from '@/pages/ErrorPreviewPage';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import AcceptInvitationPage from '@/pages/auth/AcceptInvitationPage';
import AccountDeletedPage from '@/pages/auth/AccountDeletedPage';
import AppEntryPage from '@/pages/auth/AppEntryPage';

// Student pages
import DashboardPage from '@/pages/student/DashboardPage';
import StudentChallengesPage from '@/pages/student/StoriesPage';
import StudentConversationsPage from '@/pages/student/ConversationsPage';
import StudentChallengeDetailPage from '@/pages/student/StoryDetailPage';
import StudentResultPage from '@/pages/student/ResultPage';
import StudentProgressPage from '@/pages/student/ProgressPage';
import StudentStudyTimeEvolutionPage from '@/pages/student/StudyTimeEvolutionPage';
import StudentMonthlyChallengesPage from '@/pages/student/MonthlyStoriesPage';
import {
  LearnedWordsListPage,
  LearnedWordDetailPage,
  SavedVocabDetailPage,
} from '@/pages/student/LearnedWordsPages';
import { SavedWordsListPage, SavedWordDetailPage } from '@/pages/student/SavedWordsPages';
import { RewardsPage, RewardDetailPage, InvitePage } from '@/pages/student/RewardsInvitePages';
import StudentRankingPage from '@/pages/student/RankingPage';
import StudentProfilePage from '@/pages/student/ProfilePage';
import NotificationsPage from '@/pages/student/NotificationsPage';
import StudentSettings from '@/pages/student/SettingsPage';
import StudentLevelTestPage from '@/pages/student/LevelTestPage';
import SupportPage from '@/pages/student/SupportPage';
import ReviewPage from '@/pages/student/ReviewPage';
import OnboardingPreferencesPage from '@/pages/student/OnboardingPreferencesPage';

// Company pages
import CompanyDashboard from '@/pages/company/DashboardPage';
import Employees from '@/pages/company/EmployeesPage';
import EmployeeDetail from '@/pages/company/EmployeeDetailPage';
import Reports from '@/pages/company/ReportsPage';
import CompanyRewards from '@/pages/company/RewardsPage';
import CompanyNotifications from '@/pages/company/NotificationsPage';
import CompanyProfile from '@/pages/company/ProfilePage';
import CompanySettings from '@/pages/company/SettingsPage';

// Backoffice pages
import BackofficeDashboard from '@/pages/backoffice/DashboardPage';
import BackofficeChallengeList from '@/pages/backoffice/StoryListPage';
import BackofficeChallengeEdit from '@/pages/backoffice/StoryEditPage';
import BackofficeActivityEditor from '@/pages/backoffice/ActivityEditorPage';
import { ActivityFormPage as BackofficeActivityFormPage } from '@/pages/backoffice/ActivityFormPage';
import { StoryTypeSelectorPage as BackofficeTypeSelectorPage } from '@/pages/backoffice/StoryTypeSelectorPage';
import { ResourceEditorPage as BackofficeResourceEditor } from '@/pages/backoffice/ResourceEditorPage';
import BackofficeUserList from '@/pages/backoffice/UserListPage';
import BackofficeUserDetail from '@/pages/backoffice/UserDetailPage';
import BackofficeCompanyList from '@/pages/backoffice/CompanyListPage';
import BackofficeCompanyDetail from '@/pages/backoffice/CompanyDetailPage';
import BackofficeNotifications from '@/pages/backoffice/NotificationsPage';
import PreferencesAnalyticsPage from '@/pages/backoffice/PreferencesAnalyticsPage';
import CategoryManagementPage from '@/pages/backoffice/CategoryManagementPage';
import BackofficeRewardsPage from '@/pages/backoffice/RewardsListPage';
import BackofficeNotificationsHealth from '@/pages/backoffice/NotificationsHealthPage';
import BackofficeEmails from '@/pages/backoffice/EmailsPage';
import BackofficeFeedbackList from '@/pages/backoffice/FeedbackListPage';
import BackofficeFeedbackDetail from '@/pages/backoffice/FeedbackCampaignDetailPage';
import BackofficeProfile from '@/pages/backoffice/ProfilePage';
import BackofficeSettings from '@/pages/backoffice/SettingsPage';
import { ConversationListPage as BackofficeConversationList } from '@/pages/backoffice/ConversationListPage';
import { ConversationEditPage as BackofficeConversationEdit } from '@/pages/backoffice/ConversationEditPage';
import { ConversationContentPage as BackofficeConversationContent } from '@/pages/backoffice/ConversationContentPage';
import { ConversationContentFormPage as BackofficeConversationContentForm } from '@/pages/backoffice/ConversationContentFormPage';
import { ConversationActivityEditorPage as BackofficeConversationActivityEditor } from '@/pages/backoffice/ConversationActivityEditorPage';
import { ConversationActivityFormPage as BackofficeConversationActivityForm } from '@/pages/backoffice/ConversationActivityFormPage';
import { StoryResourceFormPage as BackofficeChallengeResourceForm } from '@/pages/backoffice/StoryResourceFormPage';
import { GenerateStoryPage as BackofficeGenerateChallenge } from '@/pages/backoffice/GenerateStoryPage';
import { GenerateConversationPage as BackofficeGenerateConversation } from '@/pages/backoffice/GenerateConversationPage';
import { StoryPreviewPage as BackofficeChallengePreview } from '@/pages/backoffice/StoryPreviewPage';


// El placeholder de analytics ha sido reemplazado por PreferencesAnalyticsPage.

// Router configuration with nested routes.
// createAppRouter elige BrowserRouter (web) o HashRouter (nativo/Capacitor).
export const router = createAppRouter(
  [
  {
    // Boundary raíz: cualquier error de render en una ruta muestra la pantalla
    // de error con la marca (RouteErrorBoundary) en vez del default de RR.
    errorElement: <RouteErrorBoundary />,
    children: [
  // Raíz: resolver el auth y navegar a home (según rol/dispositivo) o a login,
  // igual que el deep link /app. NO poner aquí un <Navigate> incondicional a
  // login: la app nativa (HashRouter) arranca SIEMPRE en '/', y ese redirect
  // mandaba al login en cada arranque en frío (cierre de app, muerte del
  // proceso en background, reload OTA) aunque la sesión siguiera viva en
  // localStorage/Preferences — el famoso "me pide login cada vez".
  {
    path: ROUTES.HOME,
    element: <AppEntryPage />,
  },

  // Deep link "abrir la app" (emails de bienvenida / resumen semanal).
  // /app está registrado como App Link (AASA + assetlinks). Renderiza una página
  // real (spinner) que, tras resolver el auth, navega una sola vez al dashboard
  // (si hay sesión) o al login. NO usar <Navigate> aquí: en el arranque en frío
  // por deep link deja la pantalla en blanco (ver AppEntryPage).
  {
    path: '/app',
    element: <AppEntryPage />,
  },

  // TEMPORAL: preview de pantallas de error (borrar cuando ya no haga falta)
  {
    path: '/preview/errors',
    element: (
      <AuthI18nLayout>
        <ErrorPreviewPage />
      </AuthI18nLayout>
    ),
  },

  // Public routes (Authentication)
  {
    path: ROUTES.AUTH.LOGIN,
    element: (
      <AuthI18nLayout>
        <LoginPage />
      </AuthI18nLayout>
    ),
  },
  {
    path: ROUTES.AUTH.REGISTER,
    element: (
      <AuthI18nLayout>
        <RegisterPage />
      </AuthI18nLayout>
    ),
  },
  {
    path: ROUTES.AUTH.FORGOT_PASSWORD,
    element: (
      <AuthI18nLayout>
        <ForgotPasswordPage />
      </AuthI18nLayout>
    ),
  },
  {
    path: ROUTES.AUTH.RESET_PASSWORD,
    element: (
      <AuthI18nLayout>
        <ResetPasswordPage />
      </AuthI18nLayout>
    ),
  },
  {
    path: ROUTES.AUTH.ACCEPT_INVITATION,
    element: (
      <AuthI18nLayout>
        <AcceptInvitationPage />
      </AuthI18nLayout>
    ),
  },
  {
    path: ROUTES.AUTH.ACCOUNT_DELETED,
    element: (
      <AuthI18nLayout>
        <AccountDeletedPage />
      </AuthI18nLayout>
    ),
  },

  // Preview historia como estudiante (solo backoffice, abre en nueva pestaña)
  {
    path: '/preview/story/:id',
    element: (
      <ProtectedRoute allowedRoles={['backoffice']}>
        <BackofficeChallengePreview />
      </ProtectedRoute>
    ),
  },

  // Protected Student routes — unified layout (PageTransition persists across dashboard ↔ story)
  {
    path: '/student',
    element: (
      <ProtectedRoute allowedRoles={['student']}>
        <StudentAreaWrapper />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'stories',
        element: <StudentChallengesPage />,
      },
      {
        path: 'conversations/:id',
        element: <StudentChallengeDetailPage />,
      },
      {
        path: 'conversations',
        element: <StudentConversationsPage />,
      },
      {
        path: 'progress/study-time',
        element: <StudentStudyTimeEvolutionPage />,
      },
      {
        path: 'progress/stories-month',
        element: <StudentMonthlyChallengesPage />,
      },
      {
        path: 'progress/learned-words/:period/word/:wordId',
        element: <LearnedWordDetailPage />,
      },
      {
        path: 'progress/learned-words/:period/saved/:savedId',
        element: <SavedVocabDetailPage />,
      },
      {
        path: 'progress/learned-words/:period',
        element: <LearnedWordsListPage />,
      },
      {
        path: 'saved-words/:savedId',
        element: <SavedWordDetailPage />,
      },
      {
        path: 'saved-words',
        element: <SavedWordsListPage />,
      },
      {
        path: 'rewards/:rewardId',
        element: <RewardDetailPage />,
      },
      {
        path: 'rewards',
        element: <RewardsPage />,
      },
      {
        path: 'invite',
        element: <InvitePage />,
      },
      {
        path: 'progress',
        element: <StudentProgressPage />,
      },
      {
        path: 'ranking',
        element: <StudentRankingPage />,
      },
      {
        path: 'profile',
        element: <StudentProfilePage />,
      },
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },
      {
        path: 'settings',
        element: <StudentSettings />,
      },
      {
        path: 'support',
        element: <SupportPage />,
      },
      {
        path: 'level-test',
        element: <StudentLevelTestPage />,
      },
      {
        path: 'review',
        element: <ReviewPage />,
      },
      {
        path: 'onboarding/preferences',
        element: <OnboardingPreferencesPage />,
      },
      {
        path: 'story/:id',
        element: <StudentChallengeDetailPage />,
      },
      {
        path: 'result/:id',
        element: <StudentResultPage />,
      },
    ],
  },

  // Protected Company Admin routes (nested)
  {
    path: '/company',
    element: (
      // backoffice entra aquí para administrar una empresa concreta (ver
      // CompanyLayout: exige tener una empresa abierta en el scope).
      <ProtectedRoute allowedRoles={['company_admin', 'backoffice']}>
        <CompanyLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: <CompanyDashboard />,
      },
      {
        path: 'employees',
        element: <Employees />,
      },
      {
        path: 'employees/:id',
        element: <EmployeeDetail />,
      },
      {
        path: 'reports',
        element: <Reports />,
      },
      {
        path: 'rewards',
        element: <CompanyRewards />,
      },
      {
        path: 'notifications',
        element: <CompanyNotifications />,
      },
      {
        path: 'profile',
        element: <CompanyProfile />,
      },
      {
        path: 'settings',
        element: <CompanySettings />,
      },
    ],
  },

  // Protected Backoffice routes (nested)
  {
    path: '/backoffice',
    element: (
      <ProtectedRoute allowedRoles={['backoffice']}>
        <BackofficeLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: <BackofficeDashboard />,
      },
      {
        path: 'stories',
        element: <BackofficeChallengeList />,
      },
      {
        path: 'stories/select-type',
        element: <BackofficeTypeSelectorPage />,
      },
      {
        path: 'stories/generate',
        element: <BackofficeGenerateChallenge />,
      },
      {
        path: 'stories/create',
        element: <BackofficeChallengeEdit />,
      },
      {
        path: 'stories/:id/edit',
        element: <BackofficeChallengeEdit />,
      },
      {
        path: 'stories/:storyId/activities',
        element: <BackofficeActivityEditor />,
      },
      {
        path: 'stories/:storyId/activities/new',
        element: <BackofficeActivityFormPage />,
      },
      {
        path: 'stories/:storyId/activities/:activityId/edit',
        element: <BackofficeActivityFormPage />,
      },
      {
        path: 'stories/:storyId/resources',
        element: <BackofficeResourceEditor />,
      },
      {
        path: 'stories/:storyId/resources/new',
        element: <BackofficeChallengeResourceForm />,
      },
      {
        path: 'stories/:storyId/resources/:resourceId/edit',
        element: <BackofficeChallengeResourceForm />,
      },
      {
        path: 'conversations/generate',
        element: <BackofficeGenerateConversation />,
      },
      {
        path: 'conversations/create',
        element: <BackofficeConversationEdit />,
      },
      {
        path: 'conversations/:resourceId/content/new',
        element: <BackofficeConversationContentForm />,
      },
      {
        path: 'conversations/:resourceId/content/:contentId/edit',
        element: <BackofficeConversationContentForm />,
      },
      {
        path: 'conversations/:resourceId/content',
        element: <BackofficeConversationContent />,
      },
      {
        path: 'conversations/:resourceId/activities/new',
        element: <BackofficeConversationActivityForm />,
      },
      {
        path: 'conversations/:resourceId/activities/:activityId/edit',
        element: <BackofficeConversationActivityForm />,
      },
      {
        path: 'conversations/:resourceId/activities',
        element: <BackofficeConversationActivityEditor />,
      },
      {
        path: 'conversations/:id/edit',
        element: <BackofficeConversationEdit />,
      },
      {
        path: 'conversations',
        element: <BackofficeConversationList />,
      },
      {
        path: 'users',
        element: <BackofficeUserList />,
      },
      {
        path: 'users/:id',
        element: <BackofficeUserDetail />,
      },
      {
        path: 'companies',
        element: <BackofficeCompanyList />,
      },
      {
        path: 'companies/:id',
        element: <BackofficeCompanyDetail />,
      },
      {
        path: 'analytics',
        element: <PreferencesAnalyticsPage />,
      },
      {
        path: 'analytics/preferences',
        element: <PreferencesAnalyticsPage />,
      },
      {
        path: 'categories',
        element: <CategoryManagementPage />,
      },
      {
        path: 'rewards',
        element: <BackofficeRewardsPage />,
      },
      {
        path: 'notifications/health',
        element: <BackofficeNotificationsHealth />,
      },
      {
        path: 'notifications',
        element: <BackofficeNotifications />,
      },
      {
        path: 'emails',
        element: <BackofficeEmails />,
      },
      {
        path: 'feedback',
        element: <BackofficeFeedbackList />,
      },
      {
        path: 'feedback/:campaignId',
        element: <BackofficeFeedbackDetail />,
      },
      {
        path: 'profile',
        element: <BackofficeProfile />,
      },
      {
        path: 'settings',
        element: <BackofficeSettings />,
      },
    ],
  },

  // 404 fallback
  {
    path: '*',
    element: (
      <AuthI18nLayout>
        <NotFoundPage />
      </AuthI18nLayout>
    ),
  },
    ],
  },
]
);

export default router;
